<template>
  <AdminLayout>
    <template #topbar-search>
      <div class="flex flex-column ">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
        </IconField>
      </div>
    </template>
    <template #header>
      <Breadcrumb :items="items" />
      <Button label="Export" class="accent-button" @click="ExportTutors" />
    </template>
    <div v-if="currentView !== 'List'" class="back-section">
      <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2" @click="goBack" />
    </div>
    <div class="container">
      <div v-if="currentView === 'List'" class="flex justify-content-end mb-4 md:px-4">
        <div class="flex align-items-center gap-2">
          <label class="Label text-center">Sort By:</label>
          <Select v-model="selectedSort" :options="sortOptions" optionLabel="label" optionValue="value" placeholder=""
            class="w-36" />
        </div>
      </div>
      <TutorList v-if="currentView === 'List'" :tableData="tableData" :isLoading="isLoading"
        :first="(currentPage - 1) * rowsPerPage" :rowsPerPage="rowsPerPage" :totalRecords="totalRecords"
        :currentPage="currentPage" @page-change="onPageChange" @view-profile="viewProfile"
        @status-change="onStatusChange" @delete-student="showDeleteConfirmationDialog" />
      <TutorPublicProfile v-if="currentView === 'View profile'" :profileDetails="profileDetails"
        :enrolledStudents="enrolledStudents" :previousEnrolledStudents="previousEnrolledStudents"
        :feedbacks="feedbacks" :reviewsPagination="reviewsPagination" @change-review-page="onReviewPageChange" />
      <DeleteConfirmationDialog v-model:visible="showDeleteConfirmation" :selectedCount="selectedTutors.length"
        :loading="deleteLoading" @confirm="handleDeleteConfirm" />
      <Toast />
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import TutorList from "@/components/dashboard/admin/tutors/tutorList.vue";
import TutorPublicProfile from "@/components/dashboard/admin/tutors/tutorPublicProfile.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue"
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Toast from 'primevue/toast';
import Select from 'primevue/select';
import { useToast } from 'primevue/usetoast';
import DeleteConfirmationDialog from '@/components/dashboard/admin/confirmationDialog.vue';
import { getTutors, updateTutorStatus, getTutorProfile, getEnrolledStudents, getPreviousStudents, exportTutors, deleteTutors } from "@/api/admin";
import { getStudentReviews, fetchPublicProfiles } from "@/api/tutor";
import { notifySuccess } from '@/utils/notification';


const currentView = ref("List");
const profileDetails = ref(null);
const enrolledStudents = ref(null);
const previousEnrolledStudents = ref(null);
const feedbacks = ref(null);
const reviewsPagination = ref({ totalReviews: 0, page: 1, totalPages: 1, limit: 5 });
const currentTutorId = ref(null);
const tableData = ref([]);
const isLoading = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const showDeleteConfirmation = ref(false);
const deleteLoading = ref(false);
const selectedTutors = ref([]);
const toast = useToast();
const selectedSort = ref("newest");
const sortOptions = ref([
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Name: A-Z', value: 'a-z' },
  { label: 'Name: Z-A', value: 'z-a' },
]);

const fetchTutors = async () => {
  try {
    isLoading.value = true;
    const response = await getTutors({
      page: currentPage.value,
      limit: rowsPerPage.value,
      searchTerm: searchValue.value,
      sortBy: selectedSort.value
    });

    tableData.value = response.data.data.tutors.map(tutor => ({
      ...tutor,
      isActive: tutor.isActive ?? true,
      teachingLanguage: Array.isArray(tutor.teachingLanguage)
        ? tutor.teachingLanguage.join(', ')
        : tutor.teachingLanguage,
    }));
    totalRecords.value = response.data.data.pagination.totalTutors;
  } catch (error) {
    console.error("Failed to fetch students:", error);
  } finally {
    isLoading.value = false;
  }
};

const items = computed(() => {
  const breadcrumbItems = [{ label: "Tutors" }];

  if (currentView.value === "View profile") {
    breadcrumbItems.push({ label: profileDetails.value.username });
  }

  return breadcrumbItems;
});
watch(searchValue, () => {
  currentPage.value = 1;
  fetchTutors();
});

watch(selectedSort, () => {
  currentPage.value = 1
  fetchTutors();
});

onMounted(fetchTutors);

const onStatusChange = async (rowData) => {
  try {
    await updateTutorStatus(rowData._id);
    fetchTutors();
  } catch (err) {
    console.error('Failed to update status', err);
  }
};

// The single-profile endpoint doesn't include rating aggregates, so pull
// averageRating / reviewCount from the tutor list and match by id.
const mergeReviewStats = async (tutorId) => {
  try {
    const res = await fetchPublicProfiles();
    const tutors = res.data?.data?.tutors ?? [];
    const match = tutors.find(t => t._id === tutorId);
    if (match && profileDetails.value) {
      profileDetails.value.averageRating = match.averageRating;
      profileDetails.value.reviewCount = match.reviewCount;
    }
  } catch (err) {
    console.error("Failed to fetch tutor review stats:", err);
  }
};

const fetchReviews = async (tutorId, page = 1) => {
  try {
    currentTutorId.value = tutorId;
    const res = await getStudentReviews(tutorId, page, reviewsPagination.value.limit);
    feedbacks.value = res.data.data.reviewsList ?? [];
    if (res.data.data.pagination) {
      reviewsPagination.value = res.data.data.pagination;
    }
  } catch (err) {
    console.error("Failed to fetch reviews:", err);
  }
};

const onReviewPageChange = (page) => {
  if (currentTutorId.value) {
    fetchReviews(currentTutorId.value, page);
  }
};

const viewProfile = async (rowData) => {
  try {
    const [profile, students, previousStudents] = await Promise.allSettled([
      getTutorProfile(rowData._id),
      getEnrolledStudents(rowData._id),
      getPreviousStudents(rowData._id)
    ]);
    profileDetails.value = profile.value.data.data ?? {};
    enrolledStudents.value = students.value.data.data ?? [];
    previousEnrolledStudents.value = previousStudents.value.data.data ?? [];
    await mergeReviewStats(rowData._id);
    await fetchReviews(rowData._id, 1);

  } catch (err) {
    console.error("Unexpected error:", err);
  }
  currentView.value = "View profile";
};

const ExportTutors = async () => {
  try {
    // const res = await exportTutors({ searchTerm: searchValue.value });
    const res = await exportTutors({ searchTerm: searchValue.value, sortBy: selectedSort.value });
    const filePath = res.data.data.downloadLink;
    const link = document.createElement("a");
    link.href = filePath;
    link.download = "tutors.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifySuccess("Success!", "The file has been downloaded.");
  } catch (err) {
    console.error("Failed to export", err);
  }
}
const showDeleteConfirmationDialog = (tutorIds) => {
  selectedTutors.value = tutorIds;
  showDeleteConfirmation.value = true;
};

const handleDeleteConfirm = async () => {
  deleteLoading.value = true;
  try {
    await deleteTutors({ tutorIds: selectedTutors.value });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Successfully deleted ${selectedTutors.value.length} tutor(s)`,
      life: 3000
    });
    await fetchTutors();
    showDeleteConfirmation.value = false;
    selectedTutors.value = [];
  } catch (err) {
    console.error("Failed to delete tutor", err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete tutor(s)',
      life: 3000
    });
  } finally {
    deleteLoading.value = false;
  }
};
const onPageChange = ({ page, limit }) => {
  currentPage.value = page;
  rowsPerPage.value = limit;
  fetchTutors();
};
const showList = () => {
  currentView.value = 'List';
};

const goBack = () => {
  showList();
};
</script>

<style>
.container {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.back-section {
  display: flex;
  align-items: center;
}

.back-button {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}

.back-button:hover {
  color: var(--gray-600) !important;
  background-color: var(--gray-200) !important;
}

.back-button:active {
  color: var(--gray-600) !important;
}

.pi-arrow-left {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}
</style>